import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import React from 'react';

import type { GroupBuying } from '@/types/database/groupBuying';
import type { GroupBuyingOrders } from '@/types/database/groupBuyingOrders';
import type { User } from '@/types/database/users';

import CancelGroupOrder from '../CancelGroupOrder';
import type { TableValuesFromOrder } from '../types';

const sortOrderTime = (a: TableValuesFromOrder, b: TableValuesFromOrder) => {
  const aOrderTime = dayjs(a.orderTime);
  const bOrderTime = dayjs(b.orderTime);
  if (aOrderTime.isBefore(bOrderTime)) {
    return -1;
  } else if (aOrderTime.isAfter(bOrderTime)) {
    return 1;
  } else {
    return 0;
  }
};

export const getTableDataSource = (args: {
  users: User[];
  groupBuyingItems: GroupBuying[];
  orders: GroupBuyingOrders[];
  listType: '現在團購' | '歷史資料';
}): TableValuesFromOrder[] => {
  const { users, groupBuyingItems, orders, listType } = args;

  const tableDataSource = orders.map((order) => {
    const foundGroupBuyingItem = groupBuyingItems.find((item) => {
      return item._id === order.groupBuyingId;
    });
    const foundResident = users.find((user) => {
      return user._id === order.userId;
    });

    const { orderTime, paymentType, paymentAmount } = order;

    const returnOrder: TableValuesFromOrder = {
      ...order,
      itemName: foundGroupBuyingItem?.itemName ?? '',
      householdNumber: foundResident?.householdNumbers
        ? foundResident.householdNumbers[0]
        : '',
      residentName: foundResident?.name ?? '',
      orderTime: dayjs(orderTime).format('YYYY/MM/DD HH:mm'),
      paymentAmount:
        paymentType === '信用卡' ? `${paymentAmount}元` : `${paymentAmount}點`,
    };

    if (listType === '現在團購') {
      returnOrder.action = ['取消'];
    }

    if (listType === '歷史資料') {
      returnOrder.groupBuyingStatus =
        foundGroupBuyingItem && foundGroupBuyingItem.status;
    }

    return returnOrder;
  });

  return tableDataSource;
};

export const getTableColumns = (listType: '現在團購' | '歷史資料') => {
  const tableColumns: ColumnsType<TableValuesFromOrder> = [
    {
      title: '團購名稱',
      dataIndex: 'itemName',
    },
    {
      title: '付款狀態',
      dataIndex: 'paymentStatus',
    },
    {
      title: '戶號',
      dataIndex: 'householdNumber',
    },
    {
      title: '住戶',
      dataIndex: 'residentName',
    },
    {
      title: '下單日期(年/月/日)',
      dataIndex: 'orderTime',
      width: listType === '現在團購' ? '16%' : 'auto',
      sorter: {
        compare: sortOrderTime,
      },
    },
    {
      title: '數目',
      dataIndex: 'purchaseAmount',
    },
    {
      title: '付費方式',
      dataIndex: 'paymentType',
    },
    {
      title: '金額',
      dataIndex: 'paymentAmount',
    },
  ];

  if (listType === '現在團購') {
    tableColumns.push({
      title: '操作',
      dataIndex: 'action',
      fixed: 'right',
      render: (_, record) => {
        return <CancelGroupOrder order={record} />;
      },
    });
  }

  if (listType === '歷史資料') {
    tableColumns.push({
      title: '團購狀態',
      dataIndex: 'groupBuyingStatus',
    });
  }

  return tableColumns;
};
